import i18n from '../i18n';
import {
  fetchPushConfig,
  registerPushSubscription,
  removePushSubscription,
} from '../api/push';

const PROMPT_DISMISSED_KEY = 'thetavern_push_prompt_dismissed_at';
const PROMPT_ENABLED_KEY = 'thetavern_push_prompt_enabled';
const SUBSCRIPTION_SYNCED_KEY = 'thetavern_push_subscription_synced_at';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';

  return Notification.permission;
};

export const shouldShowPushPrompt = () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'default') return false;
  if (window.localStorage.getItem(PROMPT_ENABLED_KEY) === 'true') return false;

  const dismissedAt = Number(window.localStorage.getItem(PROMPT_DISMISSED_KEY) || 0);
  if (!dismissedAt) return true;

  return Date.now() - dismissedAt > 14 * 24 * 60 * 60 * 1000;
};

export const dismissPushPrompt = () => {
  window.localStorage.setItem(PROMPT_DISMISSED_KEY, String(Date.now()));
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);

  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

export const getCurrentPushSubscription = async () => {
  if (!isPushSupported()) return null;

  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
};

const getPushRegistration = async () => {
  const registration = await navigator.serviceWorker.ready;

  await registration.update?.();
  return registration;
};

const subscribeToPush = async (registration, publicKey) =>
  registration.pushManager.subscribe({
    applicationServerKey: urlBase64ToUint8Array(publicKey),
    userVisibleOnly: true,
  });

const currentLocale = () =>
  i18n.language || document.documentElement.lang || navigator.language;

const rememberPushSync = () => {
  window.localStorage.setItem(SUBSCRIPTION_SYNCED_KEY, String(Date.now()));
  window.localStorage.setItem(PROMPT_ENABLED_KEY, 'true');
};

export const enablePushNotifications = async ({ forceNewSubscription = false } = {}) => {
  if (!isPushSupported()) {
    return { status: 'unsupported' };
  }

  const config = await fetchPushConfig();
  if (!config.configured || !config.publicKey) {
    return { status: 'server-unconfigured' };
  }

  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

  if (permission !== 'granted') {
    return { status: permission };
  }

  const registration = await getPushRegistration();
  const existing = await registration.pushManager.getSubscription();
  if (forceNewSubscription && existing) {
    await existing.unsubscribe();
  }

  const subscription =
    !forceNewSubscription && existing
      ? existing
      : await subscribeToPush(registration, config.publicKey);

  await registerPushSubscription({
    locale: currentLocale(),
    subscription: subscription.toJSON(),
  });

  rememberPushSync();

  return { status: 'granted', subscription };
};

export const syncGrantedPushSubscription = async () => {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    return { status: 'skipped' };
  }

  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return enablePushNotifications();
  }

  await registerPushSubscription({
    locale: currentLocale(),
    subscription: subscription.toJSON(),
  });
  rememberPushSync();

  return { status: 'synced', subscription };
};

export const shouldRefreshPushSubscription = () => {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;

  const syncedAt = Number(window.localStorage.getItem(SUBSCRIPTION_SYNCED_KEY) || 0);
  if (!syncedAt) return true;

  return Date.now() - syncedAt > 24 * 60 * 60 * 1000;
};

export const disablePushNotifications = async () => {
  const subscription = await getCurrentPushSubscription();

  if (!subscription) {
    window.localStorage.removeItem(PROMPT_ENABLED_KEY);
    return { status: 'removed' };
  }

  await removePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
  window.localStorage.removeItem(PROMPT_ENABLED_KEY);

  return { status: 'removed' };
};
